import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { setSentryUser } from '../bootstrap/sentry';
import { StatSigInitialization } from '../bootstrap/StatSigInitialization';
import { RequestErrorType } from '../clients/fetchHelper';
import { useUserAccount } from '../clients/gateway-client/gatewayClient';
import { getSelectedSiteFromLocalStorage, Site, SiteProvider } from '../hooks/useSite';
import { postMessageToParent } from '../message-bus/postMessageToParent';
import { isAtlassianUser } from '../utils/isAtlassianUser';
import AuthLoadingScreen from './AuthLoadingScreen';

export const SITE_LOADING_TEST_ID = 'site-loading';
export const USER_LOADING_TEST_ID = 'user-loading';

export default function RequireAuth({
	Component,
	LoginComponent,
	publicRoutes = [],
}: {
	Component: React.ComponentType<any>;
	LoginComponent?: React.ComponentType<{ hasError: boolean }>;
	publicRoutes?: string[];
}): React.JSX.Element {
	const [site, setSite] = useState<Site | undefined>(getSelectedSiteFromLocalStorage());
	const [_, setIsInitialized] = useState(false);
	const { pathname } = useLocation();
	const anonymousId: string | undefined =
		JSON.parse(localStorage.getItem('ajs_anonymous_id') ?? '""') || undefined;
	const {
		data: userAccount,
		error: userError,
		isLoading: isUserLoading,
		isFetching,
		isSuccess,
		isError,
	} = useUserAccount();

	const isPublicRoute = publicRoutes.some((route) => pathname.startsWith(route));

	if (isPublicRoute) {
		const isQueryRunning = isFetching || (!isSuccess && !isError);

		// block Statsig initialization until we know the final user state
		// instead, show a loading screen while waiting for the user account to load
		if (isQueryRunning) {
			return <AuthLoadingScreen />;
		}

		// if the user is logged out, use the anonymous id for initialization
		return (
			<StatSigInitialization analyticsAnonymousId={anonymousId} userAccount={userAccount}>
				<Component site={site} userAccount={userAccount} />
			</StatSigInitialization>
		);
	}

	const handleSiteChange = async (newSite: Site): Promise<void> => {
		setSite(newSite);
	};

	if (isUserLoading || (!userAccount && !userError)) {
		return (
			<UFOLoadHold name="user-authentication-loading">
				<div data-testid={USER_LOADING_TEST_ID}></div>
			</UFOLoadHold>
		);
	}

	if (userError) {
		return LoginComponent ? (
			<StatSigInitialization>
				<LoginComponent hasError={userError.errorType !== RequestErrorType.AUTH_ERROR} />
			</StatSigInitialization>
		) : (
			<></>
		);
	}

	postMessageToParent({
		type: 'USER_UPDATE',
		value: { userId: userAccount.accountId, isAtlassianUser: isAtlassianUser(userAccount.email) },
	});
	setSentryUser(userAccount.accountId);

	return (
		<StatSigInitialization
			analyticsAnonymousId={anonymousId}
			onInitialized={() => setIsInitialized(true)}
			site={site}
			userAccount={userAccount}
		>
			<SiteProvider
				featureGatesSite={site}
				onSiteChange={handleSiteChange}
				userAccount={userAccount}
			>
				<Component site={site} userAccount={userAccount} />
			</SiteProvider>
		</StatSigInitialization>
	);
}
